<script setup>
import { computed, inject } from 'vue';
import { hasLink, shortDate, styleVars } from './shared.js';
import { NO_POSTS_YET } from '../blockContent.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// No posts is no block for a visitor, heading included, and a post with
// no photo gets no empty photo frame; the editor keeps both prompts
// (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
// A list of the org's own posts with none in it says so (Mus,
// 2026-10-12): the News page and any page built on this block would
// otherwise be a heading over nothing. Hand-typed items stay off.
const source = computed(() => (props.config.source === 'auto' ? 'auto' : 'manual'));

// Two sources, exactly as Classic reads them: hand-typed items, or the
// latest published posts mapped into the same card shape. The meta
// line is assembled here rather than in the template so a manual card
// with a category and no date reads the same as one with both.
const items = computed(() => {
    if (source.value === 'auto') {
        const posts = props.site_data?.news_posts || [];
        // 'all' is the News page itself, which lists every post it was handed.
        const max = props.config.max_posts === 'all'
            ? posts.length
            : Math.max(1, Math.min(12, Number(props.config.max_posts) || 6));
        return posts.slice(0, max).map((p) => card({
            image: p.hero_image || '',
            category: '',
            date: shortDate(p.published_at),
            title: p.title,
            excerpt: p.excerpt || '',
            href: p.href,
        }));
    }
    return (props.config.items || []).map(card);
});

function card(it) {
    const meta = [it.category, it.date].map((s) => String(s ?? '').trim()).filter(Boolean).join(' · ');
    return { ...it, meta };
}

const featured = computed(() => (props.config.layout === 'featured' ? (items.value[0] || null) : null));
const gridItems = computed(() => (featured.value ? items.value.slice(1) : items.value));
const more = computed(() => props.config.more_link || {});
</script>

<template>
    <section v-if="items.length || source === 'auto' || editing" :id="config.anchor_id || null" class="th-block th-sec th-news" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.subtitle || hasLink(more)" class="th-head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                    <p v-if="config.subtitle" class="th-lede th-news__sub">{{ config.subtitle }}</p>
                </div>
                <a v-if="hasLink(more)" :href="more.href || '/news'" class="th-more">{{ more.label }} →</a>
            </div>

            <component v-if="featured" :is="featured.href ? 'a' : 'div'" :href="featured.href || null" class="th-news__featured">
                <div v-if="featured.image || editing" class="th-media th-news__featured-media">
                    <img v-if="featured.image" :src="featured.image" alt="">
                    <span v-else class="th-media__label">Featured photo</span>
                </div>
                <div>
                    <p v-if="featured.meta" class="th-eyebrow th-news__featured-meta">{{ featured.meta }}</p>
                    <h3 class="th-h2 th-h2--md th-news__featured-title">{{ featured.title }}</h3>
                    <p v-if="featured.excerpt" class="th-body th-news__featured-excerpt">{{ featured.excerpt }}</p>
                    <span class="th-news__read">Read article →</span>
                </div>
            </component>

            <div v-if="gridItems.length" class="th-news__grid">
                <component v-for="(item, i) in gridItems" :key="i"
                           :is="item.href ? 'a' : 'div'"
                           :href="item.href || null"
                           class="th-news__card">
                    <div v-if="item.image || editing" class="th-media th-news__media">
                        <img v-if="item.image" :src="item.image" alt="">
                        <span v-else class="th-media__label">Article photo</span>
                    </div>
                    <p v-if="item.meta" class="th-eyebrow th-news__meta">{{ item.meta }}</p>
                    <h3 class="th-h3 th-news__title">{{ item.title }}</h3>
                    <p v-if="item.excerpt" class="th-small">{{ item.excerpt }}</p>
                </component>
            </div>

            <p v-if="items.length === 0 && (editing || source === 'auto')" class="th-small th-news__empty">
                {{ !editing ? NO_POSTS_YET
                    : source === 'auto'
                        ? 'No published posts yet. Publish a post in the News dashboard and it shows up here.'
                        : 'Add news items in the editor.' }}
            </p>
        </div>
    </section>
</template>

<style scoped>
.th-news__sub { margin-top: 12px; }

.th-news__featured {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr));
    gap: clamp(20px, 4vw, 48px);
    align-items: center;
    margin-bottom: clamp(32px, 5vw, 56px);
    text-decoration: none !important;
}
.th-news__featured-media { aspect-ratio: 3 / 2; }
.th-news__featured-meta { margin-bottom: 14px; }
.th-news__featured-title { margin-bottom: 14px; }
.th-news__featured-excerpt { margin-bottom: 20px; }
.th-news__read { font-weight: 600; border-bottom: 2px solid var(--accent); }

.th-news__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: clamp(24px, 3vw, 40px) clamp(20px, 3vw, 32px);
}
.th-news__card { display: flex; flex-direction: column; gap: 12px; text-decoration: none !important; }
.th-news__media { aspect-ratio: 16 / 10; }
.th-news__meta { margin: 0; }
.th-news__title { font-size: 22px; }
.th-news__empty { font-style: italic; }
</style>
